import React, { useState, useEffect } from 'react';
import { X, User, Camera, Trash2, Key, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { API_BASE_URL } from '../config';
import './EnterpriseDMS.css';

function ProfileSettingsModal({ user, onClose, onUpdateUser }) {
    const token = localStorage.getItem('dms_auth_token') || sessionStorage.getItem('dms_auth_token');
    let decodedToken = {};
    try {
        if (token) {
            let base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
            while (base64.length % 4) base64 += '=';
            decodedToken = JSON.parse(decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));
        }
    } catch (e) { console.error(e); }

    const fullUser = { ...user, ...decodedToken };

        const [activeTab, setActiveTab] = useState('personal');
    const [formData, setFormData] = useState({ name: fullUser?.name || '' });
    const [passwordData, setPasswordData] = useState({ current: '', newPassword: '', confirm: '' });
    const [photoUrl, setPhotoUrl] = useState(fullUser?.picture || fullUser?.avatar || null);
    useEffect(() => {
        setPhotoUrl(fullUser?.picture || fullUser?.avatar || null);
    }, [fullUser?.picture, fullUser?.avatar]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    const isSSO = fullUser?.provider && fullUser.provider !== 'email';

    const resolvePhotoUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }
        const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
        const path = url.startsWith('/') ? url : '/' + url;
        return `${base}${path}`;
    };

    const broadcastUpdate = React.useCallback((updatedUser, newToken) => {
        if (newToken) {
            localStorage.setItem('dms_auth_token', newToken);
            sessionStorage.removeItem('dms_auth_token'); // Ensure we only use one
        }
        window.dispatchEvent(new Event('dms-auth-updated'));
        if (onUpdateUser && updatedUser) onUpdateUser(updatedUser);
    }, [onUpdateUser]);

    const handleSavePersonal = async () => {
        setLoading(true);
        setMessage(null);
        try {
            const token = localStorage.getItem('dms_auth_token') || sessionStorage.getItem('dms_auth_token');
            const res = await fetch(`${API_BASE_URL}/api/auth/update-profile`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ name: formData.name, picture: photoUrl || '' })
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ type: 'success', text: 'Profile updated successfully!' });
                if (data.token) {
                    broadcastUpdate(data.user, data.token);
                }
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to update profile' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const handleSavePassword = async () => {
        if (passwordData.newPassword !== passwordData.confirm) {
            setMessage({ type: 'error', text: 'New passwords do not match.' });
            return;
        }
        if (!passwordData.current || !passwordData.newPassword) {
            setMessage({ type: 'error', text: 'Please fill in all password fields.' });
            return;
        }

        setLoading(true);
        setMessage(null);
        try {
            const token = localStorage.getItem('dms_auth_token') || sessionStorage.getItem('dms_auth_token');
            const res = await fetch(`${API_BASE_URL}/api/auth/update-password`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ currentPassword: passwordData.current, newPassword: passwordData.newPassword })
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ type: 'success', text: 'Password updated successfully!' });
                setPasswordData({ current: '', newPassword: '', confirm: '' });
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to update password' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const handlePhotoUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Frontend Validation
        if (!file.type.match('image/(jpeg|png|webp)')) {
            setMessage({ type: 'error', text: 'Only JPG, PNG, and WEBP images are allowed.' });
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setMessage({ type: 'error', text: 'Image size must be less than 10MB.' });
            return;
        }

        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // Recommended target size: 256x256
            const size = 256;
            canvas.width = size;
            canvas.height = size;

            // Crop to center square
            const sourceWidth = img.width;
            const sourceHeight = img.height;
            const minSize = Math.min(sourceWidth, sourceHeight);
            const sourceX = (sourceWidth - minSize) / 2;
            const sourceY = (sourceHeight - minSize) / 2;

            ctx.drawImage(
                img,
                sourceX, sourceY, minSize, minSize,
                0, 0, size, size
            );

            // Compress to JPEG with 0.85 quality
            const optimizedBase64 = canvas.toDataURL('image/jpeg', 0.85);
            setPhotoUrl(optimizedBase64);
            setMessage(null);
        };
        img.onerror = () => {
            setMessage({ type: 'error', text: 'Failed to process image.' });
        };

        const reader = new FileReader();
        reader.onloadend = () => {
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    };

    return (
        <div className="modal-overlay" onClick={onClose} style={{ zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--header-bg, rgba(0, 0, 0, 0.4))', backdropFilter: 'blur(12px)' }}>
            <div className="profile-settings-modal" onClick={e => e.stopPropagation()} style={{
                background: 'var(--card-hover-bg, var(--surface))',
                backdropFilter: 'var(--glass-blur)',
                borderRadius: '24px',
                width: '600px',
                maxWidth: '90vw',
                maxHeight: '85vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                border: 'var(--glass-border)',
                boxShadow: 'var(--glass-shadow)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem 2rem', borderBottom: '1px solid var(--border-glass)' }}>
                    <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-main)' }}>Profile Settings</h2>
                    <button onClick={onClose} style={{ background: 'var(--surface-light)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', cursor: 'pointer', padding: '8px', borderRadius: '50%', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'} onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}><X size={18} /></button>
                </div>

                <div style={{ display: 'flex', borderBottom: '1px solid var(--border-glass)', padding: '0 2rem', gap: '2rem', background: 'var(--header-bg)' }}>
                    <button
                        onClick={() => setActiveTab('personal')}
                        style={{ padding: '1.25rem 0', background: 'transparent', border: 'none', color: activeTab === 'personal' ? 'var(--text-main)' : 'var(--text-muted)', borderBottom: activeTab === 'personal' ? '3px solid #2563eb' : '3px solid transparent', cursor: 'pointer', fontWeight: activeTab === 'personal' ? 700 : 500, fontSize: '0.95rem', transition: 'all 0.3s', display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                        <User size={18} style={{ color: activeTab === 'personal' ? '#3b82f6' : 'inherit' }} />
                        Personal Info
                    </button>
                    {!isSSO && (
                        <button
                            onClick={() => setActiveTab('security')}
                            style={{ padding: '1.25rem 0', background: 'transparent', border: 'none', color: activeTab === 'security' ? 'var(--text-main)' : 'var(--text-muted)', borderBottom: activeTab === 'security' ? '3px solid #2563eb' : '3px solid transparent', cursor: 'pointer', fontWeight: activeTab === 'security' ? 700 : 500, fontSize: '0.95rem', transition: 'all 0.3s', display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <Key size={18} style={{ color: activeTab === 'security' ? '#3b82f6' : 'inherit' }} />
                            Password & Security
                        </button>
                    )}
                </div>

                <div style={{ padding: '2rem', overflowY: 'auto' }}>
                    {message && (
                        <div style={{ padding: '1rem', marginBottom: '1.5rem', borderRadius: '12px', background: message.type === 'success' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: message.type === 'success' ? '#10b981' : '#ef4444', border: `1px solid ${message.type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`, display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
                            {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                            <span>{message.text}</span>
                        </div>
                    )}

                    {activeTab === 'personal' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div style={{ display: 'flex', gap: '2rem', alignItems: 'center', background: 'var(--surface)', padding: '1.5rem', borderRadius: '16px', border: '1px solid var(--border-glass)' }}>
                                <div style={{ width: '90px', height: '90px', borderRadius: '50%', overflow: 'hidden', border: '3px solid var(--border-glass)', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', flexShrink: 0, position: 'relative' }}>
                                    {photoUrl ? (
                                        <img src={resolvePhotoUrl(photoUrl)} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #1e3a8a, #0ea5e9)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', fontWeight: '800' }}>
                                            {formData.name ? formData.name.charAt(0).toUpperCase() : 'U'}
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    <h4 style={{ margin: 0, color: 'var(--text-main)', fontSize: '1.1rem', fontWeight: 700 }}>Profile Picture</h4>
                                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                        <label style={{ boxSizing: 'border-box', height: '36px', margin: 0, border: '1px solid transparent', background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)', color: '#fff', padding: '0 1.25rem', borderRadius: '10px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.3s', boxShadow: '0 4px 15px rgba(37,99,235,0.3)' }} onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
                                            <Camera size={16} /> Change Photo
                                            <input type="file" accept="image/jpeg, image/png, image/webp" style={{ display: 'none' }} onChange={handlePhotoUpload} />
                                        </label>
                                        <button onClick={() => setPhotoUrl(null)} style={{ boxSizing: 'border-box', height: '36px', margin: 0, background: 'transparent', border: '1px solid rgba(239,68,68,0.4)', color: '#ef4444', padding: '0 1.25rem', borderRadius: '10px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.3s' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                            <Trash2 size={16} /> Remove
                                        </button>
                                    </div>
                                    <small style={{ color: 'var(--text-muted)' }}>JPG, PNG or WEBP. Max size 2MB.</small>
                                </div>
                            </div>

                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block', fontWeight: 600, fontSize: '0.9rem' }}>Full Name</label>
                                <input type="text" className="form-control" style={{ width: '100%' }} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                            </div>

                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block', fontWeight: 600, fontSize: '0.9rem' }}>Email Address</label>
                                <input type="text" className="form-control" style={{ width: '100%', opacity: 0.7, cursor: 'not-allowed' }} value={fullUser?.email || ''} readOnly />
                                <small style={{ color: 'var(--text-muted)', display: 'block', marginTop: '0.5rem', fontSize: '0.8rem' }}>Email address cannot be changed.</small>
                            </div>

                            <button
                                onClick={handleSavePersonal}
                                disabled={loading}
                                style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)', border: 'none', color: '#fff', padding: '14px', borderRadius: '12px', fontWeight: 600, fontSize: '1rem', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '1rem', opacity: loading ? 0.7 : 1, transition: 'all 0.2s', boxShadow: '0 4px 15px rgba(37,99,235,0.2)' }}
                                onMouseEnter={e => { if (!loading) e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                onMouseLeave={e => { if (!loading) e.currentTarget.style.transform = 'none'; }}
                            >
                                <Save size={18} /> {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    )}

                    {activeTab === 'security' && !isSSO && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            <div style={{ padding: '1.25rem', background: 'rgba(234,179,8,0.1)', borderLeft: '4px solid #eab308', borderRadius: '8px', marginBottom: '0.5rem' }}>
                                <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '1rem' }}><Key size={18} style={{ color: '#eab308' }} /> Password Update</h4>
                                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Updating your password will revoke all other active sessions and trusted devices.</p>
                            </div>

                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block', fontWeight: 600, fontSize: '0.9rem' }}>Current Password</label>
                                <input type="password" className="form-control" style={{ width: '100%' }} value={passwordData.current} onChange={e => setPasswordData({ ...passwordData, current: e.target.value })} />
                            </div>

                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block', fontWeight: 600, fontSize: '0.9rem' }}>New Password</label>
                                <input type="password" className="form-control" style={{ width: '100%' }} value={passwordData.newPassword} onChange={e => setPasswordData({ ...passwordData, newPassword: e.target.value })} />
                            </div>

                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block', fontWeight: 600, fontSize: '0.9rem' }}>Confirm New Password</label>
                                <input type="password" className="form-control" style={{ width: '100%' }} value={passwordData.confirm} onChange={e => setPasswordData({ ...passwordData, confirm: e.target.value })} />
                            </div>

                            <button
                                onClick={handleSavePassword}
                                disabled={loading}
                                style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)', border: 'none', color: '#fff', padding: '14px', borderRadius: '12px', fontWeight: 600, fontSize: '1rem', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '1rem', opacity: loading ? 0.7 : 1, transition: 'all 0.2s', boxShadow: '0 4px 15px rgba(37,99,235,0.2)' }}
                                onMouseEnter={e => { if (!loading) e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                onMouseLeave={e => { if (!loading) e.currentTarget.style.transform = 'none'; }}
                            >
                                <Save size={18} /> {loading ? 'Updating...' : 'Update Password'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ProfileSettingsModal;
